import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  AppContext,
  COMMON_CONSTANTS,
  EventEmitter,
  EventEmitterWithIndex,
  getCustomViewValues,
  getTranslatedText,
  Loader,
  useActivityTracker
} from '@nile/components';
import {
  AccountClient,
  GET_ENTITY_PROPERTIES,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION
} from '@nile/graphql';
import ListViewColumns from '@nile/mangifera/src/Containers/SheetContainers/ListViewColumns';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getFilterType,
  getDataType
} from '@nile/client_management/src/Pebbles/InvoiceContainer';
import ClientContext from '@nile/client_management/src/Contexts/ClientContext';

function getLabel(name) {
  switch (name) {
    case 'invoiceId':
      return 'client.navigation.invoice.list.invoiceId';
    case 'invoicePaymentTerm.paymentTermName':
      return 'attribute.invoicePaymentTermName';
    case 'emailOperationalStatus':
      return 'client.navigation.invoice.list.emailStatus';
    case 'pushedToIntegratedAppStatus':
      return 'client.navigation.invoice.list.integrationStatus';
    default:
      return name;
  }
}

function useGetColumns({ viewId, module }) {
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const intl = useIntl();

  const {
    getModuleMeta,
    moduleMetaInput,
    moduleMetaClient,
    moduleMetaResponseKey
  } = getCustomViewValues(module);

  const [columnsData, setColumnsData] = useState({
    columns: [],
    selectedColumns: [],
    availableColumns: []
  });

  const allColumnsList = [
    'displayId',
    'invoiceDate',
    'dueDate',
    'statusEnum',
    'totalAmount',
    'client.name',
    'site.name',
    'pushedToIntegratedAppStatus',
    'paymentDate',
    'paymentMethod.paymentMethodName',
    'invoicePaymentTerm.paymentTermName',
    'emailOperationalStatus'
  ];

  const defaultColumnsList = [
    'displayId',
    'invoiceDate',
    'dueDate',
    'statusEnum',
    'totalAmount',
    'client.name',
    'site.name',
    'paymentMethod.paymentMethodName',
    'emailOperationalStatus'
  ];
  const [allColumns, setAllColumns] = useState([]);

  const [defaultColumns, setDefaultColumns] = useState([]);

  const { loading: entitiesLoading, data: allEntitiesMeta } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const { loading: viewMetaLoading, data: metaData } = useQuery(getModuleMeta, {
    variables: moduleMetaInput ? { input: moduleMetaInput } : {},
    client: moduleMetaClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    onCompleted(data) {
      if (data?.[moduleMetaResponseKey]) {
        if (data?.[moduleMetaResponseKey]?.selectAttributes)
          setAllColumns(
            data?.[moduleMetaResponseKey]?.selectAttributes || allColumnsList
          );
        if (data?.[moduleMetaResponseKey]?.defaultSelectedAttributes)
          setDefaultColumns(
            data?.[moduleMetaResponseKey]?.defaultSelectedAttributes ||
              defaultColumnsList
          );
      }
    }
  });

  const [
    getPersonalization,
    { data: personalisedColumns, loading, error }
  ] = useLazyQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    variables: {
      userId: user?.userId,
      category: `${module?.toLowerCase()}-columnPersonilsation`,
      name: viewId
    },
    fetchPolicy: 'cache-and-network'
  });

  const handleColumns = () => {
    const columnsFromPersonalization =
      personalisedColumns?.getPersonalizationByCategoryAndName?.value &&
      JSON.parse(
        personalisedColumns?.getPersonalizationByCategoryAndName?.value
      )?.columns;

    const moduleEntities =
      allEntitiesMeta?.getEntitiesMeta.find((item) => {
        return item.name?.toLowerCase() === module?.toLowerCase();
      })?.attributes || [];

    // const selectedColumns = defaultColumns;
    const selectedColumns = columnsFromPersonalization ?? defaultColumns;

    const availableColumns = [];

    const constructedColumns = [];

    for (let i = 0; i < allColumns.length; i++) {
      const col = allColumns[i];

      let selectedAttr;

      if (col.split('.').length > 1) {
        const attr = col.split('.')[0];
        const attrCol = col.split('.')[1];
        const moduleSubEntites =
          allEntitiesMeta?.getEntitiesMeta.find((item) => {
            return item.name?.toLowerCase() === attr?.toLowerCase();
          })?.attributes || [];

        selectedAttr = moduleSubEntites.find(
          (attr) => attr.name?.toLowerCase() === attrCol?.toLowerCase()
        );
      } else {
        selectedAttr = moduleEntities.find(
          (attr) => attr.name?.toLowerCase() === col?.toLowerCase()
        );
      }

      const columnData = {
        ...(selectedAttr || {}),
        id: col,
        name: col,
        label: getTranslatedText(
          intl,
          selectedAttr?.attributePlaceHolder?.label || getLabel(col)
        ),
        dataType: getDataType({ name: col }),
        filterType: getFilterType({ name: col }),
        disable: defaultColumns.includes(col),
        selected: !!selectedColumns.includes(col),
        defaultColumn: defaultColumns.includes(col)
      };

      if (!selectedColumns.includes(col)) {
        availableColumns.push(col);
      }

      constructedColumns.push(columnData);
    }

    setColumnsData({
      columns: constructedColumns.sort(
        (col, nextCol) =>
          selectedColumns.indexOf(col.name) -
          selectedColumns.indexOf(nextCol.name)
      ),
      selectedColumns: selectedColumns,
      availableColumns: availableColumns
    });
  };

  useEffect(() => {
    EventEmitterWithIndex.subscribe(
      'refetchInvoiceColumns',
      getPersonalization
    );
    return function cleanup() {
      EventEmitterWithIndex.unsubscribe('refetchInvoiceColumns');
    };
  }, []);

  useEffect(() => {
    if (viewId) getPersonalization();
  }, [viewId]);

  useEffect(() => {
    handleColumns();
  }, [personalisedColumns, allColumns, defaultColumns]);

  return {
    loading: !!entitiesLoading && !!viewMetaLoading && !!loading,
    ...columnsData
  };
}

const InvoiceListColumns = ({
  module = 'INVOICE',
  viewId: viewIdFromProps
}) => {
  const intl = useIntl();
  const appContext = useContext(AppContext);
  const { user } = appContext;

  const context = useContext(ClientContext);

  const viewId = viewIdFromProps || context?.value?.activeView?.id;

  const flags = useFlags();

  const { columns, loading: columnLoading } = useGetColumns({
    module,
    viewId
  });

  const columnDataRef = useRef(null);
  columnDataRef.current = columns;

  const personalisedColumnsRef = useRef(null);

  const [invoiceColumnPersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    update(cache, { data }) {
      let personalisedData = cache.readQuery({
        query: GET_PERSONALIZATION_BY_NAME,
        variables: {
          userId: user?.userId,
          category: `${module?.toLowerCase()}-columnPersonilsation`,
          name: viewId
        }
      });
      console.log('personalisedData => ', personalisedData);
      cache.writeQuery({
        query: GET_PERSONALIZATION_BY_NAME,
        data: {
          getPersonalizationByCategoryAndName: {
            ...personalisedData?.getPersonalizationByCategoryAndName,
            value: JSON.stringify({ columns: personalisedColumnsRef.current })
          }
        }
      });
    },
    onCompleted({ savePersonalization }) {
      if (savePersonalization) {
        // refetchColumns();
        EventEmitterWithIndex.dispatch('refetchInvoiceColumns');
      }
    },
    onError() {
      // toast('error', 'Something went wrong');
    }
  });

  const setDefaultColumns = (data) => {
    const columnData = [];
    for (let i = 0; i < data.length; i++) {
      columnData.push(data[i]?.name);
    }
    const personalizedColumn = {
      user: {
        firstName: user?.firstName,
        userId: user?.userId,
        email: user?.email,
        lastName: user?.lastName
      },
      category: `${module?.toLowerCase()}-columnPersonilsation`,
      name: viewId || '',
      value: {
        columns: columnData
      }
    };

    console.log('personalizedColumn => ', personalizedColumn);

    personalisedColumnsRef.current = columnData;

    const selectedColumns = columnDataRef?.current?.filter(
      (col) => !!col?.selected
    );

    const columnAdded = selectedColumns?.length > (columnData?.length || 0);

    useActivityTracker({
      event: columnAdded
        ? 'INVOICE_LIST_COLUMN_ADDED'
        : 'INVOICE_LIST_COLUMN_REMOVED'
    });

    invoiceColumnPersonalization({
      variables: {
        ...personalizedColumn,
        value: JSON.stringify(personalizedColumn.value)
      }
    });
  };

  const updateParent = (data) => {
    setDefaultColumns(data);
  };

  return (
    <>
      {columnLoading ? (
        <div className="mr-10 ml-10">
          <Loader />
        </div>
      ) : (
        <ListViewColumns
          text={getTranslatedText(intl, 'ticket.icon.column')}
          // activator='Columns'
          columns={columns?.length}
          sections={[
            {
              header: getTranslatedText(intl, 'ticket.columns.fields'),
              items: columnDataRef.current
            }
          ]}
          meta={{
            heading: getTranslatedText(intl, 'invoiceAudit.header.reorder'),
            selectedTitle: getTranslatedText(intl, 'asset.columns')
          }}
          selectedItems={columns?.filter((col) => !!col?.selected)}
          updateParent={updateParent}
        />
      )}
    </>
  );
};

export default InvoiceListColumns;

export { useGetColumns as getColumns };
